import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormLabel from "@mui/material/FormLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";

import type {
  AssignedLimitation,
  AssignedLimitationInput,
  LimitationDefinition,
} from "src/shared/api-types/limitations";

import Button from "src/components/Button";
import { getDefinitionDisplayName, getOptionDisplayValue } from "src/features/limitations/limitationDisplay";

interface OperationLimitationsDialogProps {
  open: boolean;
  definitions: LimitationDefinition[];
  assignedLimitations: AssignedLimitation[];
  onClose: () => void;
  onSave: (limitations: AssignedLimitationInput[]) => void;
}

const OperationLimitationsDialog = ({
  open,
  definitions,
  assignedLimitations,
  onClose,
  onSave,
}: OperationLimitationsDialogProps) => {
  const { t } = useTranslation();
  const [selectedByDefinition, setSelectedByDefinition] = useState<Record<number, string>>({});

  const visibleDefinitions = useMemo(() => {
    const assignedDefinitionIds = new Set(assignedLimitations.map((limitation) => limitation.definitionId));
    return definitions
      .filter((definition) => !definition.archived || assignedDefinitionIds.has(definition.id))
      .sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
  }, [assignedLimitations, definitions]);

  useEffect(() => {
    if (!open) return;
    const next: Record<number, string> = {};
    assignedLimitations.forEach((limitation) => {
      next[limitation.definitionId] = String(limitation.optionId ?? "");
    });
    setSelectedByDefinition(next);
  }, [assignedLimitations, open]);

  const handleClearAll = () => {
    setSelectedByDefinition({});
  };

  const handleSave = () => {
    const limitations = visibleDefinitions
      .map((definition): AssignedLimitationInput | null => {
        const selected = selectedByDefinition[definition.id];
        if (!selected) return null;
        return { definitionId: definition.id, optionId: Number(selected) };
      })
      .filter((limitation): limitation is AssignedLimitationInput => limitation !== null);
    onSave(limitations);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>{t("limitations.title")}</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <FormLabel component="legend" sx={{ fontSize: "0.875rem" }}>
            {t("limitations.select_limitations")}
          </FormLabel>
          <Button size="small" onClick={handleClearAll} sx={{ fontSize: "0.75rem", p: "2px 8px" }}>
            {t("limitations.clear_all")}
          </Button>
        </Box>
        {visibleDefinitions.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            {t("limitations.no_active_configured")}
          </Typography>
        ) : (
          visibleDefinitions.map((definition, index) => (
            <Box
              key={definition.id}
              sx={{
                mb: index < visibleDefinitions.length - 1 ? 1 : 0,
                pb: index < visibleDefinitions.length - 1 ? 1 : 0,
                borderBottom: index < visibleDefinitions.length - 1 ? "1px solid #eaeaea" : "none",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="body2" sx={{ mr: 1, width: 120, color: "text.secondary" }}>
                  {getDefinitionDisplayName(definition, t)}:
                </Typography>
                <Select
                  fullWidth
                  size="small"
                  value={selectedByDefinition[definition.id] ?? ""}
                  onChange={(event) =>
                    setSelectedByDefinition((prev) => ({ ...prev, [definition.id]: String(event.target.value) }))
                  }
                  displayEmpty
                  sx={{ fontSize: "0.875rem" }}
                >
                  <MenuItem value="">{t("limitations.no_limitation")}</MenuItem>
                  {definition.options
                    .filter((option) => definition.type !== "boolean" || option.value === true)
                    .filter((option) => !option.archived || selectedByDefinition[definition.id] === String(option.id))
                    .map((option) => (
                      <MenuItem key={option.id} value={String(option.id)}>
                        {getOptionDisplayValue(definition, option, t)}
                        {option.archived ? ` (${t("limitations.statuses.archived")})` : ""}
                      </MenuItem>
                    ))}
                </Select>
              </Box>
            </Box>
          ))
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} size="small">
          {t("cancel")}
        </Button>
        <Button onClick={handleSave} color="primary" size="small">
          {t("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OperationLimitationsDialog;
